import * as t from 'io-ts';

import {ApiType} from '../../../models/api/apiTypes';
import {CompanyModel} from '../../../models/api/companyModel';
import {fetchApiAsync} from '../../../services/http/http';
import {ResourceListPagination, ResourceTableCommonPageType} from './resourceListPaginationHelpers';
import {makeResourceListPaginationOffset, PaginationParameter} from './resourceListPaginationOffset';

/*
 * Types.
 */

interface SearchResultsWithTotal<TResource> extends ResourceTableCommonPageType<TResource> {
  total: number;
}

interface ResourceListSearchOffsetPageType<TResource> extends SearchResultsWithTotal<TResource> {
  nextOffset: number;
}

interface ResourceListPaginationSearchOffsetConfig<
  TResource,
  TSearchResults extends SearchResultsWithTotal<TResource>,
> {
  pageSize?: number;
  makeSearchUrl: (page: PaginationParameter, company: CompanyModel) => string;
  apiType: ApiType<t.Type<TSearchResults, any>>;
}

/*
 * Helpers.
 */

/**
 * Abstracts the Search Offset Limit logic to useResourceList.
 * Example: /rule_bundles?limit=50&offset=100 -> fetch rule
 * bundles from 100 to 150.
 *
 * @param config The config of the pagination.
 */
export function makeResourceListPaginationSearchOffset<
  TResource,
  TSearchResults extends SearchResultsWithTotal<TResource>,
>(
  config: ResourceListPaginationSearchOffsetConfig<TResource, TSearchResults>,
): ResourceListPagination<TResource, number, ResourceListSearchOffsetPageType<TResource>> {
  const {makeSearchUrl, apiType, pageSize} = config;

  return makeResourceListPaginationOffset<TResource, {total: number}>({
    fetchApi: async (pagination, getNextOffset, company, cancelToken) => {
      const url = makeSearchUrl(pagination, company);
      const results = await fetchApiAsync(apiType, url, {cancelToken});

      return {
        ...results,
        nextOffset: getNextOffset(pagination.offset),
      };
    },
    pageSize,
  });
}
